import { Alert, Box } from '@mui/material';
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useDeviceAlerts } from '@/api/customSensorAlerts/useDeviceAlerts';
import { ConsolidatedWaveDataItem } from '@/api/historicalData';
import { useWaveData } from '@/api/historicalData/useWaveData';
import { usePreferredTimeZone } from '@/api/userAccount';
import { waveHeightBulkIcon } from '@/assets';
import { Line, LineChart, LineDataSlice, drawLineChart } from '@/charts';
import { colorsets } from '@/charts/common';
import { addRenderersByProcessingSource } from '@/charts/helpers';
import { AlertLine } from '@/charts/types';
import { SensorIcon } from '@/components/data/SensorIcon';
import { ChartLineSelector, ChartLineSelectorProps } from '@/components/spotter/historicalDataTab/ChartLineSelector';
import {
    ChartDataDetails,
    ChartDataSelectors,
    ChartWithInfo,
    ProcessingSourceLegendItems,
} from '@/components/spotter/historicalDataTab/ChartWithInfo';
import { DataDetailsByTimestamp } from '@/components/spotter/historicalDataTab/datumDetail/DataDetailPanel';
import {
    DataLineExtent,
    DataLineMean,
    LineChartDataSummary,
} from '@/components/spotter/historicalDataTab/datumDetail/LineChartDataSummary';
import { LineChartDatumDetail } from '@/components/spotter/historicalDataTab/datumDetail/LineChartDatumDetail';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { useDateRangeContext } from '@/context/DateRangeContext';
import { useSpotterContext } from '@/context/SpotterContext';
import { useTimelineNavigationContext } from '@/context/TimelineNavigationContext';
import { useElementSize } from '@/hooks/useElementSize';
import { useTrackChartView } from '@/hooks/useIsViewedTracking';
import { useGlobalStore } from '@/store';
import { composeAlertLines } from '@/utils';

export type LineConfig = Line<ConsolidatedWaveDataItem>;

export const BULK_WAVE_HEIGHT_LINE = {
    id: 'significant-wave-height-bulk',
    label: 'Sig. wave height',
    valueKey: 'significantWaveHeight' as const,
    color: colorsets.blue,
    unitKey: 'significantWaveHeightUnits',
    unitTypeKey: 'significantWaveHeightUnitType',
} satisfies LineConfig & ChartLineSelectorProps['lines'][number];

export const PARTITION_WAVE_HEIGHT_LINES = [
    {
        id: 'significant-wave-height-swell',
        label: 'Sig. wave height',
        sensorLabel: 'Swell',
        valueKey: 'significantWaveHeightSwell' as const,
        color: colorsets.green,
        unitKey: 'partitionSignificantWaveHeightUnits',
        unitTypeKey: 'partitionSignificantWaveHeightUnitType',
    },
    {
        id: 'significant-wave-height-sea',
        label: 'Sig. wave height',
        sensorLabel: 'Sea',
        valueKey: 'significantWaveHeightSea' as const,
        color: colorsets.red,
        unitKey: 'partitionSignificantWaveHeightUnits',
        unitTypeKey: 'partitionSignificantWaveHeightUnitType',
    },
] satisfies (LineConfig & ChartLineSelectorProps['lines'][number])[];

export type SignificantWaveChartProps = {
    chartTitle: string;
    spotterId: string;
    queryEnabled?: boolean;
    showAlertLines: boolean;
    scaleToFitAlerts: boolean;
    alertLines?: AlertLine[];
    lines: LineConfig[];
    onSelectData?: (data: LineDataSlice<ConsolidatedWaveDataItem, LineConfig> | null) => void;
    onSelectTimestamp?: (timestamp: string | null, timeline?: string[]) => void;
};

export const SignificantWaveChart = ({
    chartTitle,
    spotterId,
    queryEnabled = true,
    showAlertLines,
    scaleToFitAlerts,
    alertLines,
    onSelectData,
    onSelectTimestamp,
    lines,
}: SignificantWaveChartProps) => {
    const id = useId();
    const { dateRange } = useDateRangeContext();
    const chartRef = useRef<LineChart<ConsolidatedWaveDataItem, LineConfig> | null>(null);
    const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);
    const { selectedTimestamp } = useTimelineNavigationContext();
    const selectedTimestampRef = useRef(selectedTimestamp);
    selectedTimestampRef.current = selectedTimestamp;
    useTrackChartView({
        containerEl,
        spotterId,
        chartTitle,
    });

    const timeZone = usePreferredTimeZone();
    const { data: waveData, isPending } = useWaveData({
        dateRange,
        config: { enabled: queryEnabled },
    });

    const containerRef = useRef<HTMLElement>(null);
    const containerId = `significant-wave-height-chart${id}`;
    const { width, height } = useElementSize(containerRef);

    const drawChart = useCallback(drawLineChart, []);

    const linesWithRenderers = useMemo(() => addRenderersByProcessingSource(lines, waveData), [lines, waveData]);

    useEffect(() => {
        if (isPending) {
            return;
        }
        if (!waveData) {
            return;
        }

        const newChart = drawChart<ConsolidatedWaveDataItem, LineConfig>({
            dataset: waveData,
            lines: linesWithRenderers,
            xAxisKey: 'timestamp',
            yAxisLabel: 'Significant wave height',
            containerId,
            showAlertLines,
            scaleChartToFitAllAlerts: scaleToFitAlerts,
            alertLines,
            chartWidth: width,
            chartHeight: height,
            dateRange,
            timeZone,
            onSelectData,
            onSelectTimestamp,
        });
        chartRef.current = newChart;
        newChart.setSelectedTimestamp?.(selectedTimestampRef.current ? new Date(selectedTimestampRef.current) : null);

        return () => {
            newChart.destroy();
            if (chartRef.current === newChart) {
                chartRef.current = null;
            }
        };
    }, [
        alertLines,
        containerId,
        dateRange,
        timeZone,
        drawChart,
        height,
        isPending,
        linesWithRenderers,
        onSelectData,
        onSelectTimestamp,
        scaleToFitAlerts,
        showAlertLines,
        waveData,
        width,
    ]);

    useEffect(() => {
        chartRef.current?.setSelectedTimestamp?.(selectedTimestamp ? new Date(selectedTimestamp) : null);
    }, [selectedTimestamp]);

    useLayoutEffect(() => {
        if (!containerRef.current) {
            return;
        }
        setContainerEl(containerRef.current);
    }, [waveData?.length, dateRange]);

    return <Box ref={containerRef} id={containerId} width={1} height={1} />;
};

export const SignificantWaveSection = () => {
    const { setSelectedTimestamp } = useTimelineNavigationContext();
    const { dateRange } = useDateRangeContext();
    const chartTitle = 'Significant wave height';
    const { spotterId } = useSpotterContext();
    const [activeLinesId, setActiveLinesId] = useState(
        [BULK_WAVE_HEIGHT_LINE, ...PARTITION_WAVE_HEIGHT_LINES].map((line) => line.id)
    );
    const isAlertLinesOn = useGlobalStore((state) => state.isAlertLinesOn);
    const isScaleToFitAlertLinesOn = useGlobalStore((state) => state.isScaleToFitAlertLinesOn);
    const [selectedData, setSelectedData] = useState<LineDataSlice<ConsolidatedWaveDataItem> | null>(null);

    const { data: deviceAlerts } = useDeviceAlerts({
        config: { enabled: isAlertLinesOn },
    });

    const alertLines = useMemo(
        () => composeAlertLines({ deviceAlerts, triggerTypes: ['spotter_threshold_significantwaveheight'] }),
        [deviceAlerts]
    );

    const {
        data: waveData,
        isPending,
        isFetching,
        hasPartitionData,
    } = useWaveData({
        dateRange,
    });

    const lines = useMemo(
        () =>
            hasPartitionData
                ? [{ ...BULK_WAVE_HEIGHT_LINE, sensorLabel: 'Bulk' }, ...PARTITION_WAVE_HEIGHT_LINES]
                : [BULK_WAVE_HEIGHT_LINE],
        [hasPartitionData]
    );

    const activeLines = useMemo(() => lines.filter((line) => activeLinesId.includes(line.id)), [activeLinesId, lines]);

    return (
        <CollapsibleSection
            isFetching={isFetching}
            isPending={isPending}
            title={chartTitle}
            icon={<SensorIcon src={waveHeightBulkIcon} />}
        >
            {waveData?.length ? (
                <Box>
                    <ChartWithInfo
                        legend={<ProcessingSourceLegendItems data={waveData} />}
                        chart={
                            <SignificantWaveChart
                                onSelectTimestamp={setSelectedTimestamp}
                                lines={activeLines}
                                spotterId={spotterId!}
                                chartTitle={chartTitle}
                                onSelectData={setSelectedData}
                                showAlertLines={isAlertLinesOn}
                                scaleToFitAlerts={isScaleToFitAlertLinesOn}
                                alertLines={alertLines}
                            />
                        }
                    >
                        <ChartDataSelectors>
                            <ChartLineSelector lines={lines} allLinesLabel="All data" onChange={setActiveLinesId} />
                        </ChartDataSelectors>
                        <ChartDataDetails>
                            {!selectedData && (
                                <LineChartDataSummary
                                    dateRange={dateRange}
                                    lines={activeLines}
                                    dataset={waveData}
                                    renderLineSummary={() => (
                                        <>
                                            <DataLineMean />
                                            <DataLineExtent />
                                        </>
                                    )}
                                />
                            )}
                            <DataDetailsByTimestamp
                                selectedData={selectedData}
                                datumRenderer={({ lines, datum }) => (
                                    <LineChartDatumDetail dataset={waveData} datum={datum} lines={lines} />
                                )}
                            />
                        </ChartDataDetails>
                    </ChartWithInfo>
                </Box>
            ) : (
                <Alert severity="info">No significant wave data for selected date range</Alert>
            )}
        </CollapsibleSection>
    );
};
